import { DictionaryContextType } from '../dictionaries/context/DictionaryContextTypes';
import { BankType, BrandType, NegotiationOption, NegotiationOptionAlternative, NegotiationState, NegotiationType, OutDebt, OutDebtFieldsFragment } from '../generated/graphql';
import { getContentBalanceBehindPaymentBancoBogota } from './getContentBalanceBehindPaymentBancoBogota';
import { getContentInstallmentsPlanBancoBogota } from './getContentInstallmentsPlanBancoBogota';
import { getContentInstallmentsPlanBbva } from './getContentInstallmentsPlanBbva';
import {
  getBbvaSendToBankContentText,
  getBbvaConfirmByUserContentText,
  getBbvaApprovedByBankContentText,
  getBbvaRejectedByBankContentText,
} from './getContentTextStatesInstallmentsPlanBbva';
import { getContentTotalPaymentBancoBogota } from './getContentTotalPaymentBancoBogota';
import { getContentKeysAcpkMovistarMoney, getContentTotalPaymentMovistarMoney } from './getContentTotalPaymentMovistarMoney';

type CustomContentTextType = {
  gT: (key: string, context?: DictionaryContextType) => string,
  outDebt: OutDebt,
  negOption: NegotiationOption,
  outDebts?: Array<OutDebtFieldsFragment>,
  alternative?: NegotiationOptionAlternative,
  isConsequences?: boolean,
};

/*
  * Create new custom handlers for banks and negotiation types
  * This is open to extend when needed
*/
const { Bbva, MovistarMoney, BancoDeBogota } = BankType;
const { Tranquiapp } = BrandType;
const {
  InstallmentsPlan, TotalPayment, PenaltyPortfolioPaymentAgreement, BalanceBehindPayment,
} = NegotiationType;
const { RejectedByBank, ApprovedByBank, SentToBank, ConfirmedByUser } = NegotiationState;
const contentHandlers: { [key: string]: (props: CustomContentTextType) => string | null } = {
  [`${Tranquiapp}-${Bbva}-${InstallmentsPlan}-${RejectedByBank}`]: getBbvaRejectedByBankContentText,
  [`${Tranquiapp}-${Bbva}-${InstallmentsPlan}-${ApprovedByBank}`]: getBbvaApprovedByBankContentText,
  [`${Tranquiapp}-${Bbva}-${InstallmentsPlan}-${SentToBank}`]: getBbvaSendToBankContentText,
  [`${Tranquiapp}-${Bbva}-${InstallmentsPlan}-${ConfirmedByUser}`]: getBbvaConfirmByUserContentText,
  [`${Tranquiapp}-${Bbva}-${InstallmentsPlan}-`]: getContentInstallmentsPlanBbva,
  [`${Tranquiapp}-${MovistarMoney}-${TotalPayment}-${ConfirmedByUser}`]: getContentTotalPaymentMovistarMoney,
  [`${Tranquiapp}-${BancoDeBogota}-${TotalPayment}-${ConfirmedByUser}`]: getContentTotalPaymentBancoBogota,
  [`${Tranquiapp}-${BancoDeBogota}-${InstallmentsPlan}-`]: getContentInstallmentsPlanBancoBogota,
  [`${Tranquiapp}-${BancoDeBogota}-${InstallmentsPlan}-${ConfirmedByUser}`]: getContentInstallmentsPlanBancoBogota,
  [`${Tranquiapp}-${BancoDeBogota}-${InstallmentsPlan}-${SentToBank}`]: getContentInstallmentsPlanBancoBogota,
  [`${Tranquiapp}-${BancoDeBogota}-${InstallmentsPlan}-${ApprovedByBank}`]: getContentInstallmentsPlanBancoBogota,
  [`${Tranquiapp}-${BancoDeBogota}-${TotalPayment}-`]: getContentTotalPaymentBancoBogota,
  [`${Tranquiapp}-${BancoDeBogota}-${BalanceBehindPayment}-`]: getContentBalanceBehindPaymentBancoBogota,
  [`${Tranquiapp}-${BancoDeBogota}-${BalanceBehindPayment}-${ConfirmedByUser}`]: getContentBalanceBehindPaymentBancoBogota,
  [`${Tranquiapp}-${BancoDeBogota}-${BalanceBehindPayment}-${SentToBank}`]: getContentBalanceBehindPaymentBancoBogota,
  [`${Tranquiapp}-${BancoDeBogota}-${BalanceBehindPayment}-${ApprovedByBank}`]: getContentBalanceBehindPaymentBancoBogota,
};

const keysHandlers: { [key: string]: (props: CustomContentTextType) => string } = {
  [`${Tranquiapp}-${MovistarMoney}-${PenaltyPortfolioPaymentAgreement}-${ConfirmedByUser}`]: getContentKeysAcpkMovistarMoney,
  [`${Tranquiapp}-${BancoDeBogota}-${PenaltyPortfolioPaymentAgreement}-`]: getContentKeysAcpkMovistarMoney,
};

/**
 * Provides a custom function to handle dictionary text
 * @param gT Hook from useDictionary to search keys.
 * @param negOption Negotiation from user.
 * @param outDebt OutDebt from user.
 * @returns Content from dictionary if found, otherwise undefined.
 */
export const getCustomContentText = (props : CustomContentTextType) => {
  const { negOption, outDebt: { bank, negotiation, brand } } = props;
  const key = `${brand}-${bank}-${negOption.type}-${negotiation?.state || ''}`;
  const handler = contentHandlers[key];
  if (handler) {
    return handler(props);
  }

  return undefined;
};

/**
 * Provides a custom function to handle dictionary text
 * @param gT Hook from useDictionary to search keys.
 * @param negOption Negotiation from user.
 * @param outDebt OutDebt from user.
 * @returns Keys from dictionary if found, otherwise undefined.
 */
export const getCustomContentKeys = (props : CustomContentTextType) => {
  const { negOption, outDebt: { bank, negotiation, brand } } = props;
  const key = `${brand}-${bank}-${negOption.type}-${negotiation?.state || ''}`;
  const handler = keysHandlers[key];
  if (handler) {
    return handler(props);
  }

  return undefined;
};
